import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useRealtime } from '@/hooks/useRealtime';
import AuthGuard from '@/components/feature/AuthGuard';
import AdminLayout from '../components/AdminLayout';

interface User {
  id: string;
  full_name: string | null;
  email: string | null;
  role: string;
  email_verified: boolean | null;
  account_status: string;
  failed_login_attempts: number;
  last_failed_login: string | null;
  created_at: string;
}

const FUNCTIONS_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL + '/functions/v1';

const roleColor = (role: string) => {
  switch (role) {
    case 'admin': return 'bg-red-100 text-red-700';
    case 'organizer': return 'bg-primary-100 text-primary-700';
    case 'accounts': return 'bg-secondary-100 text-secondary-700';
    default: return 'bg-background-200 text-foreground-500';
  }
};

export default function UserManagementPage() {
  const { profile } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ full_name: '', email: '', role: 'organizer', password: '' });
  const [showReset, setShowReset] = useState<string | null>(null);
  const [resetForm, setResetForm] = useState({ password: '', confirmPassword: '' });
  const [resetError, setResetError] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState('');
  const [lockLoading, setLockLoading] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error: err } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });
      if (err) throw err;
      setUsers(data || []);
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useRealtime<User>({
    table: 'profiles',
    onAny: () => {
      loadUsers();
    },
  });

  const handleSave = async () => {
    if (!form.full_name.trim() || !form.email.trim()) return;

    try {
      if (editId) {
        const { error: err } = await supabase
          .from('profiles')
          .update({ full_name: form.full_name.trim(), email: form.email.trim(), role: form.role })
          .eq('id', editId);
        if (err) throw err;
      } else {
        if (!form.password || form.password.length < 6) {
          setError('Password must be at least 6 characters');
          return;
        }
        const { data: authData, error: authErr } = await supabase.auth.signUp({
          email: form.email.trim(),
          password: form.password,
          options: { data: { full_name: form.full_name.trim(), role: form.role } },
        });
        if (authErr) throw authErr;
        if (authData.user) {
          await supabase.from('profiles').upsert({
            id: authData.user.id,
            full_name: form.full_name.trim(),
            email: form.email.trim(),
            role: form.role,
            email_verified: true,
          });
        }
      }
      setShowModal(false);
      setEditId(null);
      setForm({ full_name: '', email: '', role: 'organizer', password: '' });
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save user');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error: err } = await supabase.from('profiles').delete().eq('id', id);
      if (err) throw err;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const handleResetPassword = async () => {
    if (!showReset) return;
    if (!resetForm.password || resetForm.password.length < 6) {
      setResetError('Password must be at least 6 characters');
      return;
    }
    if (resetForm.password !== resetForm.confirmPassword) {
      setResetError('Passwords do not match');
      return;
    }

    setResetting(true);
    setResetError('');
    setResetSuccess('');

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const res = await fetch(
        `${FUNCTIONS_URL}/admin-reset-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ target_user_id: showReset, new_password: resetForm.password }),
        }
      );
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to reset password');
      setResetSuccess('Password reset successfully');
      setTimeout(() => {
        setShowReset(null);
        setResetForm({ password: '', confirmPassword: '' });
        setResetSuccess('');
      }, 1500);
    } catch (err: unknown) {
      setResetError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const handleLockUnlock = async (targetUser: User, action: 'lock' | 'unlock') => {
    setLockLoading(targetUser.id);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const res = await fetch(`${FUNCTIONS_URL}/admin-lock-unlock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action, target_user_id: targetUser.id }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to update account status');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update account status');
    } finally {
      setLockLoading(null);
    }
  };

  const openEdit = (u: User) => {
    setEditId(u.id);
    setForm({ full_name: u.full_name || '', email: u.email || '', role: u.role, password: '' });
    setShowModal(true);
  };

  return (
    <AuthGuard requiredRole="admin">
      <AdminLayout title="User Management" breadcrumbs={[{ label: 'Dashboard', href: '/dashboard/admin' }, { label: 'Users' }]}>
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="cursor-pointer"><i className="ri-close-line"></i></button>
          </div>
        )}

        <div className="flex justify-end mb-5">
          <button onClick={() => { setEditId(null); setForm({ full_name: '', email: '', role: 'organizer', password: '' }); setShowModal(true); }} className="px-4 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
            <i className="ri-add-line"></i>
            Add User
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-user-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-400 text-sm">No users found</p>
          </div>
        ) : (
          <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-100/50">
                    {['Name', 'Email', 'Role', 'Status', 'Failed', 'Verified', 'Created', 'Actions'].map((label) => (
                      <th key={label} className="px-4 py-3 text-left text-xs font-semibold text-foreground-500 whitespace-nowrap">{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className={`border-b border-background-100 hover:bg-background-50/50 transition-colors ${u.account_status === 'locked' ? 'bg-red-50/30' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${u.account_status === 'locked' ? 'bg-red-100' : 'bg-primary-100'}`}>
                            <span className={`text-xs font-bold ${u.account_status === 'locked' ? 'text-red-600' : 'text-primary-600'}`}>{u.full_name?.[0] || '?'}</span>
                          </div>
                          <span className="font-medium text-foreground-900">{u.full_name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-foreground-600 text-xs">{u.email}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize ${roleColor(u.role)}`}>{u.role}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold ${u.account_status === 'locked' ? 'bg-red-100 text-red-700' : 'bg-accent-100 text-accent-700'}`}>
                          {u.account_status === 'locked' ? (
                            <><i className="ri-lock-line mr-1"></i>Locked</>
                          ) : (
                            <><i className="ri-check-line mr-1"></i>Active</>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-xs font-semibold ${(u.failed_login_attempts || 0) >= 3 ? 'text-red-600' : (u.failed_login_attempts || 0) > 0 ? 'text-amber-600' : 'text-foreground-400'}`}>
                          {u.failed_login_attempts || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {u.email_verified ? (
                          <i className="ri-checkbox-circle-line text-accent-500"></i>
                        ) : (
                          <i className="ri-close-circle-line text-red-400"></i>
                        )}
                      </td>
                      <td className="px-4 py-3 text-foreground-500 text-xs whitespace-nowrap">{new Date(u.created_at).toLocaleDateString('en-IN')}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEdit(u)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-accent-50 text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer" title="Edit">
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <button onClick={() => { setShowReset(showReset === u.id ? null : u.id); setResetForm({ password: '', confirmPassword: '' }); setResetError(''); setResetSuccess(''); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-amber-50 text-foreground-400 hover:text-amber-600 transition-colors cursor-pointer" title="Reset Password">
                            <i className="ri-key-2-line text-sm"></i>
                          </button>
                          {u.account_status === 'locked' ? (
                            <button
                              onClick={() => handleLockUnlock(u, 'unlock')}
                              disabled={lockLoading === u.id}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-accent-50 text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer"
                              title="Unlock Account"
                            >
                              {lockLoading === u.id ? (
                                <div className="w-3.5 h-3.5 rounded-full border-2 border-accent-500 border-t-transparent animate-spin"></div>
                              ) : (
                                <i className="ri-lock-unlock-line text-sm"></i>
                              )}
                            </button>
                          ) : (
                            u.role !== 'admin' && u.id !== profile?.id && (
                              <>
                                <button
                                  onClick={() => handleLockUnlock(u, 'lock')}
                                  disabled={lockLoading === u.id}
                                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-600 transition-colors cursor-pointer"
                                  title="Lock Account"
                                >
                                  {lockLoading === u.id ? (
                                    <div className="w-3.5 h-3.5 rounded-full border-2 border-red-400 border-t-transparent animate-spin"></div>
                                  ) : (
                                    <i className="ri-lock-line text-sm"></i>
                                  )}
                                </button>
                                <button onClick={() => handleDelete(u.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-600 transition-colors cursor-pointer" title="Delete">
                                  <i className="ri-delete-bin-line text-sm"></i>
                                </button>
                              </>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showReset && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowReset(null)}>
            <div className="bg-background-50 rounded-2xl shadow-xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">Reset Password</h3>
              <p className="text-sm text-foreground-500 mb-4">Enter a new password for this user.</p>
              {resetError && <p className="text-sm text-red-600 mb-3">{resetError}</p>}
              {resetSuccess && <p className="text-sm text-accent-600 mb-3">{resetSuccess}</p>}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">New Password</label>
                  <input type="password" value={resetForm.password} onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 outline-none" placeholder="Min 6 characters..." />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Confirm Password</label>
                  <input type="password" value={resetForm.confirmPassword} onChange={(e) => setResetForm({ ...resetForm, confirmPassword: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 outline-none" placeholder="Re-enter password..." />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button onClick={() => setShowReset(null)} className="px-4 py-2 rounded-lg border border-background-200 text-foreground-600 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
                <button onClick={handleResetPassword} disabled={resetting} className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap">
                  {resetting ? 'Updating...' : 'Reset Password'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowModal(false)}>
            <div className="bg-background-50 rounded-2xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-background-200/70 p-5 flex items-center justify-between">
                <h3 className="font-heading text-lg font-bold text-foreground-950">{editId ? 'Edit User' : 'Add User'}</h3>
                <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Full Name</label>
                  <input type="text" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 outline-none" placeholder="Enter name..." />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Email</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 outline-none" placeholder="Enter email..." />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Role</label>
                  <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-700 text-sm focus:border-primary-400 outline-none cursor-pointer">
                    <option value="admin">Admin</option>
                    <option value="organizer">Organizer</option>
                    <option value="accounts">Accounts</option>
                  </select>
                </div>
                {!editId && (
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">Password</label>
                    <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 outline-none" placeholder="Enter password..." />
                  </div>
                )}
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-background-200 text-foreground-600 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
                  <button onClick={handleSave} className="px-5 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap">
                    {editId ? 'Save Changes' : 'Add User'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </AdminLayout>
    </AuthGuard>
  );
}